/**
 * Smart API Client
 * Combines request queue and circuit breaker for robust API calls
 */

import { requestQueue } from './requestQueue';
import { circuitBreaker } from './circuitBreaker';

/**
 * Enhanced fetch with timeout, queue, and circuit breaker
 */
export async function smartFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  // Go through circuit breaker first
  return circuitBreaker.execute(async () => {
    // Then through request queue
    return requestQueue.add(async () => {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 second timeout

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle non-OK responses
        if (!response.ok) {
          interface ErrorResponse {
            error?: string;
            detail?: string;
            message?: string;
            traceback?: string;
            [key: string]: any;
          }
          
          let errorData: ErrorResponse = {};
          let errorText = '';
          
          try {
            // Try to get response as text first
            errorText = await response.text();
            // Then try to parse as JSON
            if (errorText) {
              try {
                errorData = JSON.parse(errorText) as ErrorResponse;
              } catch (e) {
                // Not JSON, use text as error
                errorData = { detail: errorText };
              }
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            errorData = { detail: `HTTP ${response.status}: ${response.statusText}` };
          }
          
          // Extract error message from various possible fields
          const errorMessage = errorData.error || 
                               errorData.detail || 
                               errorData.message ||
                               (errorData.traceback ? `${errorData.error || 'Error'}: ${errorData.traceback.substring(0, 200)}` : null) ||
                               errorText ||
                               `HTTP ${response.status}: ${response.statusText}`;
          
          console.error(`[API Error ${response.status}]`, {
            url,
            status: response.status,
            statusText: response.statusText,
            errorData,
            errorText: errorText.substring(0, 500), // First 500 chars
            fullError: errorMessage
          });
          
          throw new Error(errorMessage);
        }

        // Handle 204 No Content
        if (response.status === 204) {
          return undefined as T;
        }

        // Parse and return JSON
        return response.json();
      } catch (error: any) {
        clearTimeout(timeoutId);

        // Handle timeout
        if (error.name === 'AbortError') {
          throw new Error('Request timeout (30 seconds). Please try again.');
        }

        // Re-throw other errors
        throw error;
      }
    });
  });
}

/**
 * Convenience wrapper with common defaults
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit & { params?: Record<string, string> } = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  // Build URL with query params
  let url = endpoint;
  if (params) {
    const queryString = new URLSearchParams(params).toString();
    url += `?${queryString}`;
  }

  return smartFetch<T>(url, fetchOptions);
}

/**
 * Log request statistics (for monitoring)
 */
export function getRequestStats() {
  return {
    queue: requestQueue.getStats(),
    circuit: circuitBreaker.getState(),
  };
}

// Export for debugging
if (typeof window !== 'undefined') {
  (window as any).__apiStats = getRequestStats;
}
